import { useCallback, useEffect, useState } from "react";
import { type AuditEntry, api } from "../lib/api.ts";

interface Props {
  syncKey: string;
}

const ACTION_COLORS: Record<string, string> = {
  "mq.create": "#60a5fa",
  "webhook.trigger": "#a78bfa",
  "scheduler.fire": "#fbbf24",
  "file.upload": "#34d399",
  "file.delete": "#f87171",
};

function fmtDate(ts: number) {
  return new Date(ts * 1000).toLocaleString();
}

function actionColor(action: string) {
  if (ACTION_COLORS[action]) return ACTION_COLORS[action];
  const prefix = action.split(".")[0] ?? "";
  const colors: Record<string, string> = {
    mq: "#60a5fa",
    webhook: "#a78bfa",
    scheduler: "#fbbf24",
    file: "#34d399",
    auth: "#fb923c",
  };
  return colors[prefix] ?? "#71717a";
}

const ACTION_FILTERS = [
  { label: "All", value: "" },
  { label: "MQ", value: "mq" },
  { label: "Webhooks", value: "webhook" },
  { label: "Scheduler", value: "scheduler" },
  { label: "Files", value: "file" },
];

export default function AuditLog({ syncKey }: Props) {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState("");
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const LIMIT = 50;

  const load = useCallback(async (off = 0) => {
    setLoading(true);
    try {
      const params: Parameters<typeof api.audit.list>[1] = {
        limit: LIMIT + 1,
        offset: off,
      };
      const res = await api.audit.list(syncKey, params);
      const raw = res.entries;
      setHasMore(raw.length > LIMIT);
      const page = raw.slice(0, LIMIT);
      if (off === 0) {
        setEntries(page);
      } else {
        setEntries((prev) => [...prev, ...page]);
      }
      setOffset(off + page.length);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [syncKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setOffset(0);
    setEntries([]);
    load(0);
  }, [load, actionFilter]);

  const filtered = actionFilter
    ? entries.filter((e) => e.action.startsWith(actionFilter))
    : entries;

  return (
    <div className="page">
      <h1>Audit Log</h1>
      <p style={{ color: "#888", fontSize: 13, marginBottom: 16 }}>
        Immutable record of actions taken by the system — MQ item creation, webhook triggers,
        scheduler fires, and more. Read-only.
      </p>

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
        {ACTION_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setActionFilter(f.value)}
            style={{
              fontSize: 11,
              padding: "2px 10px",
              opacity: actionFilter === f.value ? 1 : 0.5,
              fontWeight: actionFilter === f.value ? 600 : 400,
            }}
          >
            {f.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => load(0)}
          style={{ marginLeft: "auto", fontSize: 11, padding: "2px 10px" }}
        >
          Refresh
        </button>
      </div>

      {loading && entries.length === 0 ? (
        <p style={{ color: "#666" }}>Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="empty">No audit entries{actionFilter ? " for this filter" : ""}.</p>
      ) : (
        <>
          <div style={{ fontFamily: "monospace", fontSize: 12 }}>
            {filtered.map((e) => (
              <div
                key={e.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "160px 180px 120px 1fr",
                  gap: 8,
                  padding: "6px 0",
                  borderBottom: "1px solid rgba(255,255,255,0.04)",
                  alignItems: "baseline",
                }}
              >
                <span style={{ color: "#555", fontSize: 11 }}>{fmtDate(e.createdAt)}</span>
                <span style={{ color: actionColor(e.action), fontWeight: 500 }}>{e.action}</span>
                <span style={{ color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {e.resourceType ? `${e.resourceType}` : ""}
                  {e.resourceId ? ` ${e.resourceId.slice(0, 8)}…` : ""}
                </span>
                <span
                  style={{
                    color: "#374151",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: 11,
                  }}
                  title={e.meta}
                >
                  {e.meta !== "{}" ? e.meta.slice(0, 120) : ""}
                  {e.ip ? ` — ${e.ip}` : ""}
                </span>
              </div>
            ))}
          </div>

          {hasMore && (
            <button
              type="button"
              onClick={() => load(offset)}
              disabled={loading}
              style={{ marginTop: 12, fontSize: 12, padding: "4px 16px" }}
            >
              {loading ? "Loading…" : "Load more"}
            </button>
          )}
        </>
      )}
    </div>
  );
}
